import React, {
  Dispatch, SetStateAction, useState, useRef,
} from 'react';
import Select from 'react-select';
import { useIntl } from '@edx/frontend-platform/i18n';
import { SlRequestCloseEvent } from '@shoelace-style/shoelace/cdn/events/sl-request-close';

import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon';
import type SlAlertElement from '@shoelace-style/shoelace/dist/components/alert/alert';
import type { AuthenticatedUser, CountryListItem } from '../../types';
import { useUserLocation, useDebounce } from '../../hooks';
import { getCountriesList } from '../../utils';

import messages from './messages';

interface UserLocationSettingsProps {
  userHasRegisteredLocation: boolean;
  authenticatedUser: AuthenticatedUser | null;
  setUserHasRegisteredLocation: Dispatch<SetStateAction<boolean>>;
}

const UserLocationSettings: React.FC<UserLocationSettingsProps> = ({
  userHasRegisteredLocation,
  setUserHasRegisteredLocation,
  authenticatedUser,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryListItem | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ success: boolean, msg: string }>({ success: false, msg: '' });
  const notificationRef = useRef<SlAlertElement | null>(null);

  const intl = useIntl();
  const countries: CountryListItem[] = getCountriesList();
  const { handleSaveLocation } = useUserLocation({ authenticatedUser });

  // `aria-required` already announces "required", so the aria-label drops the "*" marker.
  const countryLabel = intl.formatMessage(messages.countryLabel);
  const countryFieldAriaLabel = countryLabel.replace(/\*/g, '').replace(/\s+/g, ' ').trim();

  // LGT-1157: the prompt is mandatory — block every dismissal route, save is the only way out.
  const handleRequestClose = (event: SlRequestCloseEvent) => {
    event.preventDefault();
  };

  const handleSetNotification = (success: boolean, msg: string, callback = () => {}) => {
    setNotification({ msg, success });
    setTimeout(() => {
      callback();
    }, 1500);
  };

  const handleCountryChange = (option: CountryListItem | null) => {
    setSelectedCountry(option);
  };

  const closeModal = () => {
    setUserHasRegisteredLocation(true);
  };

  const handleSubmit = () => {
    if (selectedCountry) {
      setSubmitting(true);
      const data = {
        country: selectedCountry?.value,
      };
      handleSaveLocation(data, () => {
        handleSetNotification(true, intl.formatMessage(messages.locationSuccess), closeModal);
      }, () => {
        setSubmitting(false);
        handleSetNotification(false, intl.formatMessage(messages.locationError));
      });
    }
  };

  useDebounce(() => {
    if (notification?.msg && notificationRef?.current) {
      if ('toast' in notificationRef.current) {
        notificationRef.current?.toast?.();
      }
    }
  }, 200, [notification]);

  return (
    <>
      <SlAlert
        ref={notificationRef}
        variant={notification?.success ? 'success' : 'danger'}
        duration={5000}
        closable
      >
        <SlIcon slot="icon" name={notification?.success ? 'check2-circle' : 'exclamation-octagon'} />
        <strong>{notification?.msg}</strong>
      </SlAlert>
      <SlDialog
        className="location-dialog"
        label={intl.formatMessage(messages.title)}
        open={!userHasRegisteredLocation}
        onSlRequestClose={handleRequestClose}
        onSlAfterHide={closeModal}
      >
        <div className="w-100">
          {intl.formatMessage(messages.description)}
          <form className="w-100 user-location-form">
            <div className="w-100 user-location-form-container">
              <label>{countryLabel}</label>
              <div className="w-100">
                <Select
                  classNamePrefix="react-select"
                  className="react-select-container"
                  name="country"
                  options={countries} // Static options go here
                  onChange={handleCountryChange}
                  isSearchable
                  required
                  aria-label={countryFieldAriaLabel}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      position: 'absolute',
                    }),
                  }}
                />
              </div>
            </div>
          </form>
        </div>
        <SlButton
          disabled={!selectedCountry}
          className="legatum-btn"
          slot="footer"
          loading={submitting}
          onClick={handleSubmit}
        >
          {intl.formatMessage(messages.saveButton)}
        </SlButton>
      </SlDialog>
    </>
  );
};

export default UserLocationSettings;
